import { Camera, ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { UrduInput } from "../../../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../../../Components/Shared/ImageSeoFields";
import {
  UNIT_OPTIONS,
  urduFor,
} from "../../../../../Utils/staticDropdownOptions";
import { colorNameToHex } from "../../utils/colorHelpers";
import ColorCard from "./ColorCard";

function formatPrice(price, currency = "PKR") {
  if (price === "" || price == null) return null;
  const n = Number(price);
  if (Number.isNaN(n)) return `${currency} ${price}`;
  return `${currency} ${n.toLocaleString()}`;
}

function unitLabel(unit) {
  const found = UNIT_OPTIONS.find((o) => o.en === unit);
  return found?.enLabel || unit || null;
}

export default function VariantCard({
  form,
  variantName,
  variantGroupIndex,
  colorsInVariant,
  variantNamesLength,
  expanded,
  onToggle,
}) {
  const {
    formData,
    validationErrors,
    variantImageInputRefs,
    variantNameInputRefs,
    setFormData,
    clearFieldErrors,
    setVariantPosition,
    handleVariantImageChange,
    handleRemoveVariantImage,
    handleVariantChange,
    removeVariantGroup,
    addColorToVariant,
  } = form;

  const isTempVariant =
    variantName && variantName.startsWith("__NEW_VARIANT_");
  const displayVariantName = isTempVariant ? "" : variantName || "";

  const firstColorIndex = colorsInVariant[0]?.originalIndex;
  const firstColor =
    firstColorIndex !== undefined
      ? formData.colors[firstColorIndex]
      : null;

  const firstNamedColor = colorsInVariant
    .map((c) => formData.colors[c.originalIndex])
    .find((c) => c?.color);

  const summaryParts = [
    formatPrice(firstColor?.price, formData.currency || "PKR"),
    firstNamedColor?.color || null,
    unitLabel(firstColor?.unit),
  ].filter(Boolean);

  const updateVariantField = (field, value, extra = {}) => {
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.map((c) =>
        c.name === variantName ? { ...c, [field]: value, ...extra } : c,
      ),
    }));
  };

  return (
    <div className={`pf-variant-row overflow-hidden ${expanded ? "expanded" : ""}`}>
      <div className="flex items-center gap-2 px-3.5 py-3">
        <button
          type="button"
          onClick={onToggle}
          className="min-w-0 flex-1 flex items-center gap-3 text-left"
        >
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-bold truncate ${
                expanded
                  ? "text-gray-900 uppercase tracking-wide"
                  : "text-gray-800"
              }`}
            >
              {displayVariantName || `Variant ${variantGroupIndex + 1}`}
              {expanded ? " — Selected" : ""}
            </p>
            {summaryParts.length > 0 && (
              <p
                className={`text-xs mt-0.5 truncate ${
                  expanded ? "text-gray-700" : "text-gray-500"
                }`}
              >
                {summaryParts.join(" • ")}
              </p>
            )}
          </div>

          {expanded && (
            <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide bg-white/80 border border-black/10 px-2 py-0.5 rounded">
              Active
            </span>
          )}

          {!expanded && firstNamedColor?.color && (
            <span
              className="pf-color-dot shrink-0"
              style={{
                backgroundColor:
                  firstNamedColor.colorCode ||
                  colorNameToHex(firstNamedColor.color) ||
                  "#9ca3af",
              }}
            />
          )}
        </button>

        {variantNamesLength > 1 && (
          <button
            type="button"
            onClick={() => removeVariantGroup(colorsInVariant)}
            className="pf-btn-remove-variant shrink-0"
          >
            <Trash2 size={14} strokeWidth={2.25} />
            Remove Variant
          </button>
        )}

        <button
          type="button"
          onClick={onToggle}
          className="pf-variant-chevron shrink-0"
          aria-label={expanded ? "Collapse variant" : "Expand variant"}
        >
          {expanded ? (
            <ChevronUp size={18} strokeWidth={2.25} />
          ) : (
            <ChevronDown size={18} strokeWidth={2.25} />
          )}
        </button>
      </div>

      {expanded && (
        <div className="pf-variant-body space-y-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Variant {variantGroupIndex + 1}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="pf-label">
                Variant Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                key={`variant-name-${variantName}`}
                value={displayVariantName}
                ref={(el) => {
                  if (el && colorsInVariant[0]?.originalIndex !== undefined) {
                    variantNameInputRefs.current[
                      colorsInVariant[0].originalIndex
                    ] = el;
                  }
                }}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart;
                  const selectionEnd = input.selectionEnd;
                  const newName = e.target.value;

                  setFormData((prev) => {
                    const updatedColors = prev.colors.map((c) => {
                      if (c.name === variantName) {
                        return { ...c, name: newName };
                      }
                      return c;
                    });
                    return { ...prev, colors: updatedColors };
                  });

                  requestAnimationFrame(() => {
                    const idx = colorsInVariant[0]?.originalIndex;
                    if (idx !== undefined) {
                      const inputRef = variantNameInputRefs.current[idx];
                      if (inputRef && document.activeElement !== inputRef) {
                        inputRef.focus();
                        inputRef.setSelectionRange(
                          cursorPosition,
                          selectionEnd,
                        );
                      }
                    }
                  });

                  const idx = colorsInVariant[0]?.originalIndex;
                  if (idx !== undefined) {
                    clearFieldErrors(`colors.${idx}.name`);
                  }
                }}
                placeholder="Enter variant name"
                className={`pf-input ${
                  firstColorIndex !== undefined &&
                  validationErrors[`variant_${firstColorIndex}_name`]
                    ? "is-error"
                    : ""
                }`}
              />
              {firstColorIndex !== undefined &&
                validationErrors[`variant_${firstColorIndex}_name`] && (
                  <p className="text-red-500 text-xs mt-1">
                    {validationErrors[`variant_${firstColorIndex}_name`]}
                  </p>
                )}
              <UrduInput
                label="ویرینٹ کا نام"
                name="variantName_ur"
                value={
                  formData.colors[colorsInVariant[0]?.originalIndex]
                    ?.name_ur || ""
                }
                onChange={(e) => {
                  const newNameUr = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    colors: prev.colors.map((c) =>
                      c.name === variantName
                        ? { ...c, name_ur: newNameUr }
                        : c,
                    ),
                  }));
                }}
              />
            </div>
            <div>
              <label className="pf-label">Position</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[1-9][0-9]*"
                min="1"
                value={
                  formData.colors[colorsInVariant[0]?.originalIndex]
                    ?.position ?? ""
                }
                onChange={(e) =>
                  setVariantPosition(variantName, e.target.value)
                }
                placeholder="1, 2, 3..."
                className="pf-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="pf-label">
                Price ({formData.currency || "PKR"})
              </label>
              <input
                type="number"
                value={firstColor?.price || ""}
                onChange={(e) => updateVariantField("price", e.target.value)}
                placeholder="0"
                className="pf-input"
              />
            </div>
            <div>
              <label className="pf-label">Weight (KG)</label>
              <input
                type="number"
                value={firstColor?.weight || ""}
                onChange={(e) => updateVariantField("weight", e.target.value)}
                placeholder="e.g. 25"
                className="pf-input"
              />
            </div>
            <div>
              <label className="pf-label">Unit</label>
              <select
                value={firstColor?.unit || ""}
                onChange={(e) => {
                  const newUnit = e.target.value;
                  updateVariantField("unit", newUnit, {
                    unit_ur: urduFor(UNIT_OPTIONS, newUnit),
                  });
                }}
                className="pf-select"
              >
                <option value="">Select Unit</option>
                {UNIT_OPTIONS.map((o) => (
                  <option key={o.en} value={o.en}>
                    {o.enLabel}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="pf-label">Variant Cover Image</label>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={(el) => {
                if (el && firstColorIndex !== undefined) {
                  variantImageInputRefs.current[`variant_${variantName}`] = el;
                }
              }}
              onChange={(e) => {
                if (e.target.files?.[0] && firstColorIndex !== undefined) {
                  handleVariantImageChange(firstColorIndex, e.target.files[0]);
                }
                e.target.value = "";
              }}
            />

            {firstColor?.variantImageFile ||
            firstColor?.existingVariantImage ? (
              <div
                className={`pf-cover-preview ${
                  firstColorIndex !== undefined &&
                  validationErrors[`variant_${firstColorIndex}_variant_image`]
                    ? "border-red-400"
                    : ""
                }`}
              >
                <img
                  src={
                    firstColor.variantImageFile
                      ? URL.createObjectURL(firstColor.variantImageFile)
                      : firstColor.existingVariantImage
                  }
                  alt="Variant cover preview"
                  onError={(e) => {
                    e.target.src = "/assets/logo.png";
                    e.target.onerror = null;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-gray-800 truncate">
                    {firstColor.variantImageFile?.name || "Existing cover image"}
                  </p>
                  <p className="text-xs font-semibold text-gray-500 mt-0.5">
                    Required size: 450 × 450 px
                  </p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        const inputRef =
                          variantImageInputRefs.current[
                            `variant_${variantName}`
                          ];
                        if (inputRef) inputRef.click();
                      }}
                      className="text-xs font-bold text-brand-primary hover:underline"
                    >
                      Change Image
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (firstColorIndex !== undefined) {
                          handleRemoveVariantImage(firstColorIndex);
                        }
                      }}
                      className="text-xs font-bold text-red-600 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <ImageSeoFields
                  imageAlt={firstColor?.variantImageAlt || ""}
                  imageTitle={firstColor?.variantImageTitle || ""}
                  onAltChange={(value) => {
                    if (firstColorIndex === undefined) return;
                    handleVariantChange(firstColorIndex, "variantImageAlt", value);
                  }}
                  onTitleChange={(value) => {
                    if (firstColorIndex === undefined) return;
                    handleVariantChange(
                      firstColorIndex,
                      "variantImageTitle",
                      value,
                    );
                  }}
                  className="mt-2"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const inputRef =
                    variantImageInputRefs.current[`variant_${variantName}`];
                  if (inputRef) inputRef.click();
                }}
                className={`pf-upload-slot py-6 ${
                  firstColorIndex !== undefined &&
                  validationErrors[`variant_${firstColorIndex}_variant_image`]
                    ? "is-error"
                    : ""
                }`}
              >
                <Camera size={22} strokeWidth={1.5} />
                <span className="pf-upload-title">Upload Cover Image</span>
                <span className="pf-upload-hint">
                  Required dimensions: 450 × 450 px
                  <br />
                  JPG or PNG · Square crop recommended
                </span>
              </button>
            )}

            {firstColorIndex !== undefined &&
              validationErrors[`variant_${firstColorIndex}_variant_image`] && (
                <p className="text-red-500 text-xs font-semibold mt-1">
                  {
                    validationErrors[
                      `variant_${firstColorIndex}_variant_image`
                    ]
                  }
                </p>
              )}
          </div>

          <div>
            <div className="mb-3">
              <p className="text-[10px] font-bold uppercase tracking-wider text-brand-primary mb-0.5">
                Step 3 · Colors &amp; Gallery
              </p>
              <h5 className="text-sm font-bold text-gray-900">
                Add colors for this variant
              </h5>
              <p className="pf-step-hint">
                Each color can have its own images (450 × 450 px)
              </p>
            </div>

            <div className="space-y-3">
              {colorsInVariant.map((colorData, colorIndexInVariant) => {
                const actualIndex =
                  colorData.originalIndex !== undefined
                    ? colorData.originalIndex
                    : formData.colors.findIndex((c) => {
                        const matchingColors = formData.colors.filter(
                          (cc) => cc.name === variantName,
                        );
                        return (
                          c.name === variantName &&
                          matchingColors.indexOf(c) === colorIndexInVariant
                        );
                      });

                return (
                  <ColorCard
                    key={actualIndex}
                    form={form}
                    actualIndex={actualIndex}
                    colorIndexInVariant={colorIndexInVariant}
                    colorsInVariantLength={colorsInVariant.length}
                    variantName={variantName}
                    layout="multi"
                  />
                );
              })}
            </div>

            <div className="pf-section-actions">
              <button
                type="button"
                onClick={() => addColorToVariant(variantName)}
                className="pf-btn-add-item"
              >
                <Plus size={16} strokeWidth={2.5} />
                Add Another Color
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
